// CLIMB ONE / AURA Mock Operational Data
// This file provides mock data that simulates runtime API responses
// All values are dynamic - none are hardcoded in UI components

import type {
  AuraOperationalState,
  DecisionRoomState,
  CommandAuraState,
  ActiveJob,
  AgentStatus,
  DecisionTopic,
  ConversationMessage,
  TopicIntelligence,
  WhatMattersNowItem,
  SuggestedCommand,
  CommandContext,
  DomainState,
  EvidenceConfidence,
  ExecutionReadiness,
  ExecutionState,
} from "./types";

// ============================================
// Domain States (Generic, not SAP-specific)
// ============================================

export const mockDomainStates: DomainState[] = [
  {
    domain: "ui-ux",
    label: "UI/UX Implementation",
    status: "incomplete",
    statusLabel: "In Progress",
  },
  {
    domain: "codex-jobs",
    label: "Codex Queue",
    status: "paused",
    statusLabel: "Paused",
  },
  {
    domain: "claude-reviews",
    label: "Claude Review",
    status: "waiting",
    statusLabel: "Waiting",
  },
  {
    domain: "runtime",
    label: "Runtime",
    status: "stable",
    statusLabel: "Stable",
  },
];

// ============================================
// Evidence Confidence
// ============================================

export const mockEvidenceConfidence: EvidenceConfidence = {
  level: "high",
  score: 83,
  reason: "5 of 6 automated checks verified. Manual review pending for credential scope.",
  verifiedChecks: 5,
  totalChecks: 6,
};

// ============================================
// Execution Readiness
// ============================================

export const mockExecutionReadiness: ExecutionReadiness = {
  score: 94,
  level: "partial",
  blockers: [
    "Credential references pending verification",
    "Production deployment approval required",
  ],
  safeActions: [
    "Continue local branch implementation",
    "Run integration tests in staging",
    "Request Claude review for credential config",
  ],
  blockedActions: [
    "Production deployment",
    "Customer-facing API changes",
  ],
};

// ============================================
// Execution State (consolidated for Main Console)
// ============================================

export const mockExecutionState: ExecutionState = {
  domainStates: mockDomainStates,
  evidenceConfidence: mockEvidenceConfidence,
  executionReadiness: mockExecutionReadiness,
};

// ============================================
// What Matters Now Items (Dynamic from various sources)
// ============================================

export const mockWhatMattersNow: WhatMattersNowItem[] = [
  {
    id: "wmn-1",
    text: "One decision topic is live and requires founder input.",
    priority: "high",
    icon: "warning",
    source: "live-decision",
    relatedTopicId: "topic-1",
  },
  {
    id: "wmn-2",
    text: "Credential references are pending verification.",
    priority: "high",
    icon: "clock",
    source: "pending-approval",
    domain: "runtime",
  },
  {
    id: "wmn-3",
    text: "Codex queue remains paused for production execution.",
    priority: "medium",
    icon: "pause",
    source: "waiting-job",
    domain: "codex-jobs",
    relatedJobId: "job-2",
  },
  {
    id: "wmn-4",
    text: "Local UI implementation can continue safely.",
    priority: "low",
    icon: "check",
    source: "safe-action",
    domain: "ui-ux",
  },
  {
    id: "wmn-5",
    text: "Claude review is waiting on evidence submission.",
    priority: "medium",
    icon: "user",
    source: "reviewer-state",
    domain: "claude-reviews",
  },
];

// ============================================
// Active Jobs (Enhanced with all fields)
// ============================================

export const mockActiveJobs: ActiveJob[] = [
  {
    id: "job-1",
    title: "Admin Console UI/UX Redesign",
    status: "running",
    progress: 67,
    executor: "codex",
    worktree: "worktrees/admin-console-ui",
    branch: "feature/aura-console-v2",
    stage: "implementation",
    auditor: "claude",
    decisionRoomStatus: "live",
    decisionTopicId: "topic-1",
    riskLevel: "medium",
    lastUpdate: "2 min ago",
    startedAt: "2024-01-15T10:30:00Z",
    domain: "ui-ux",
    quickActions: [
      { id: "qa-1", label: "Open Job", icon: "eye", action: "open-job" },
      { id: "qa-2", label: "Open Worktree", icon: "folder", action: "open-worktree" },
      { id: "qa-3", label: "Decision Room", icon: "message-square", action: "open-decision" },
      { id: "qa-4", label: "Request Review", icon: "file-search", action: "request-review" },
    ],
  },
  {
    id: "job-2",
    title: "API Rate Limiting Implementation",
    status: "pending",
    executor: "codex",
    worktree: "worktrees/api-rate-limit",
    branch: "feature/rate-limiting",
    stage: "planning",
    auditor: "aura",
    decisionRoomStatus: "blocked",
    decisionTopicId: "topic-3",
    riskLevel: "high",
    lastUpdate: "15 min ago",
    domain: "runtime",
    quickActions: [
      { id: "qa-5", label: "Open Job", icon: "eye", action: "open-job" },
      { id: "qa-6", label: "View Evidence", icon: "file-search", action: "view-evidence" },
      { id: "qa-7", label: "Decision Room", icon: "message-square", action: "open-decision" },
    ],
  },
  {
    id: "job-3",
    title: "Customer Onboarding Flow",
    status: "queued",
    executor: "codex",
    branch: "feature/onboarding-v2",
    stage: "planning",
    riskLevel: "low",
    lastUpdate: "1 hour ago",
    domain: "customer-workflow",
    quickActions: [
      { id: "qa-8", label: "Open Job", icon: "eye", action: "open-job" },
      { id: "qa-9", label: "Start", icon: "play", action: "start-job" },
    ],
  },
  {
    id: "job-4",
    title: "Memory Sync Protocol Update",
    status: "paused",
    executor: "core",
    worktree: "worktrees/memory-sync",
    branch: "feature/memory-protocol-v3",
    stage: "review",
    auditor: "claude",
    decisionRoomStatus: "waiting-review",
    decisionTopicId: "topic-2",
    riskLevel: "medium",
    lastUpdate: "30 min ago",
    domain: "runtime",
    quickActions: [
      { id: "qa-10", label: "Resume", icon: "play", action: "resume-job" },
      { id: "qa-11", label: "Decision Room", icon: "message-square", action: "open-decision" },
      { id: "qa-12", label: "View Evidence", icon: "file-search", action: "view-evidence" },
    ],
  },
];

// ============================================
// Agent Statuses
// ============================================

export const mockAgentStatuses: AgentStatus[] = [
  {
    id: "aura",
    name: "AURA",
    role: "Operational Governor",
    status: "online",
    lastActive: "2024-01-15T11:00:00Z",
    currentTask: "Monitoring execution state",
  },
  {
    id: "founder-agent",
    name: "Founder Agent",
    role: "Business Context & Strategy",
    status: "online",
    lastActive: "2024-01-15T10:55:00Z",
    currentTask: "Analyzing market signals",
  },
  {
    id: "codex",
    name: "Codex",
    role: "Code Execution Engine",
    status: "busy",
    lastActive: "2024-01-15T11:00:00Z",
    currentTask: "Admin Console UI/UX Redesign",
  },
  {
    id: "claude",
    name: "Claude",
    role: "Deep Analysis & Review",
    status: "online",
    lastActive: "2024-01-15T10:50:00Z",
    currentTask: "Awaiting review request",
  },
  {
    id: "core",
    name: "Core",
    role: "System Integration Layer",
    status: "online",
    lastActive: "2024-01-15T11:00:00Z",
  },
];

// ============================================
// Main Operational State
// ============================================

export const mockOperationalState: AuraOperationalState = {
  system: {
    environment: "production",
    runtime: "stable",
    memory: "synced",
    provider: "openai",
    branch: "main",
    health: "verified",
  },
  truth: {
    counts: {
      blockers: 1,
      pendingApprovals: 2,
      activeJobs: 4,
    },
    domainStates: mockDomainStates,
    runtimeStatus: "stable",
    memoryStatus: "synced",
    deepContextExpanded: false,
  },
  executionState: mockExecutionState,
  brief: {
    primaryAssessment:
      "AURA has identified one blocked decision and two safe actions.",
    belief:
      "Production execution should remain blocked until credential references are verified. Local implementation can continue safely.",
    recommendation:
      "Hold production execution. Allow Codex to continue local implementation. Request Claude review before approval.",
    whatMattersNow: mockWhatMattersNow,
    executionReadiness: mockExecutionReadiness,
    evidenceConfidence: mockEvidenceConfidence,
    requiredApprovalRole: "operations-lead",
    requiredApprovalDescription:
      "Approval required before production execution can proceed.",
  },
  jobs: mockActiveJobs,
  agents: mockAgentStatuses,
  currentUser: {
    id: "user-1",
    name: "Operations Lead",
    initials: "OL",
    role: "operations-lead",
  },
};

// ============================================
// Decision Room State
// ============================================

export const mockDecisionTopics: DecisionTopic[] = [
  {
    id: "topic-1",
    title: "Admin Console Production Deployment",
    summary: "Blocked on credential verification. AURA recommends hold.",
    status: "live",
    participants: [
      { id: "p-1", name: "AURA", type: "aura" },
      { id: "p-2", name: "Codex", type: "codex" },
      { id: "p-3", name: "Operations Lead", type: "human" },
    ],
    blockerCount: 1,
    approvalCount: 2,
    createdAt: "2024-01-15T09:00:00Z",
    updatedAt: "2024-01-15T10:55:00Z",
  },
  {
    id: "topic-2",
    title: "Memory Sync Protocol Update",
    summary: "Waiting for security review before implementation.",
    status: "waiting-review",
    participants: [
      { id: "p-1", name: "AURA", type: "aura" },
      { id: "p-4", name: "Claude", type: "claude" },
    ],
    blockerCount: 0,
    approvalCount: 1,
    createdAt: "2024-01-14T14:00:00Z",
    updatedAt: "2024-01-15T08:30:00Z",
  },
  {
    id: "topic-3",
    title: "API Rate Limiting Strategy",
    summary: "Blocked on infrastructure capacity assessment.",
    status: "blocked",
    participants: [
      { id: "p-1", name: "AURA", type: "aura" },
      { id: "p-5", name: "Founder Agent", type: "founder-agent" },
      { id: "p-2", name: "Codex", type: "codex" },
    ],
    blockerCount: 2,
    approvalCount: 0,
    createdAt: "2024-01-13T11:00:00Z",
    updatedAt: "2024-01-15T07:15:00Z",
  },
  {
    id: "topic-4",
    title: "Logging Infrastructure Upgrade",
    summary: "Successfully deployed to staging. Approved for production.",
    status: "resolved",
    participants: [
      { id: "p-1", name: "AURA", type: "aura" },
      { id: "p-2", name: "Codex", type: "codex" },
    ],
    blockerCount: 0,
    approvalCount: 0,
    createdAt: "2024-01-12T16:00:00Z",
    updatedAt: "2024-01-14T12:00:00Z",
  },
];

export const mockDecisionThread: ConversationMessage[] = [
  {
    id: "msg-1",
    source: "core",
    content: "Decision topic opened: Admin Console Production Deployment",
    timestamp: "2024-01-15T09:00:00Z",
    metadata: { coreEvent: "topic-created" },
  },
  {
    id: "msg-2",
    source: "aura",
    agentName: "AURA",
    content:
      "I have analyzed the deployment request. The admin console code is complete, but I am detecting incomplete credential references in the production environment configuration.",
    timestamp: "2024-01-15T09:05:00Z",
  },
  {
    id: "msg-3",
    source: "evidence",
    content: "Credential reference scan results attached.",
    timestamp: "2024-01-15T09:06:00Z",
    metadata: { evidenceId: "ev-1" },
  },
  {
    id: "msg-4",
    source: "codex",
    agentName: "Codex",
    content:
      "Local implementation is complete. All tests pass. I can continue with staging deployment while credential issues are resolved.",
    timestamp: "2024-01-15T09:30:00Z",
  },
  {
    id: "msg-5",
    source: "human",
    agentName: "Operations Lead",
    content:
      "Can we proceed with staging while the credential issue is investigated?",
    timestamp: "2024-01-15T10:00:00Z",
  },
  {
    id: "msg-6",
    source: "aura",
    agentName: "AURA",
    content:
      "Yes, staging deployment is safe. I recommend holding production execution until credentials are verified. Requesting Claude review for additional analysis.",
    timestamp: "2024-01-15T10:02:00Z",
  },
  {
    id: "msg-7",
    source: "risk-warning",
    content:
      "Production deployment without verified credentials poses security risk.",
    timestamp: "2024-01-15T10:03:00Z",
    metadata: { riskLevel: "high" },
  },
  {
    id: "msg-8",
    source: "claude",
    agentName: "Claude",
    content:
      "I have reviewed the credential configuration. The missing references appear to be for OAuth integration. These are required for production API calls. I recommend: 1) Verify credential storage location, 2) Confirm rotation policy, 3) Test authentication flow in staging first.",
    timestamp: "2024-01-15T10:45:00Z",
  },
  {
    id: "msg-9",
    source: "memory",
    content:
      "Related: Previous deployment required similar credential setup (see decision #127).",
    timestamp: "2024-01-15T10:46:00Z",
    metadata: { memoryKey: "decision-127" },
  },
];

export const mockTopicIntelligence: TopicIntelligence = {
  evidenceSummary:
    "3 evidence items collected. Credential scan shows 2 missing references. Test coverage is 94%.",
  evidenceItems: [
    {
      id: "ev-1",
      title: "Credential Reference Scan",
      source: "Core Security Module",
      confidence: "verified",
      summary:
        "Detected 2 undefined credential references in production config.",
      timestamp: "2024-01-15T09:06:00Z",
    },
    {
      id: "ev-2",
      title: "Test Coverage Report",
      source: "Codex CI Pipeline",
      confidence: "high",
      summary: "94% coverage. All integration tests pass in staging.",
      timestamp: "2024-01-15T09:25:00Z",
    },
    {
      id: "ev-3",
      title: "API Documentation Review",
      source: "External Reference",
      confidence: "medium",
      summary: "OAuth 2.0 flow requires client_id and client_secret.",
      timestamp: "2024-01-15T09:30:00Z",
    },
  ],
  risk: {
    level: "high",
    factors: [
      "Missing production credentials",
      "OAuth integration untested in production",
      "No rollback plan documented",
    ],
    mitigations: [
      "Verify credentials before deployment",
      "Test OAuth flow in staging",
      "Prepare rollback procedure",
    ],
  },
  impact: {
    scope: "service",
    affectedSystems: ["Admin Console", "User Authentication", "API Gateway"],
    estimatedDuration: "2-4 hours for full deployment",
  },
  approvalRequirement: {
    role: "operations-lead",
    reason:
      "Production deployment affecting external integrations requires Operations Lead approval.",
  },
  auraRecommendation: {
    action: "Hold production deployment. Continue with staging verification.",
    reasoning:
      "The credential issue is blocking but resolvable. Staging deployment is safe and will provide additional validation. Production should wait until credentials are verified and Claude review is complete.",
    confidence: "high",
  },
  history: [
    {
      id: "hist-1",
      action: "Topic created",
      actor: "System",
      actorType: "core",
      timestamp: "2024-01-15T09:00:00Z",
    },
    {
      id: "hist-2",
      action: "Evidence attached: Credential scan",
      actor: "AURA",
      actorType: "aura",
      timestamp: "2024-01-15T09:06:00Z",
    },
    {
      id: "hist-3",
      action: "Staging deployment approved",
      actor: "AURA",
      actorType: "aura",
      timestamp: "2024-01-15T10:02:00Z",
    },
    {
      id: "hist-4",
      action: "Claude review requested",
      actor: "AURA",
      actorType: "aura",
      timestamp: "2024-01-15T10:02:00Z",
    },
    {
      id: "hist-5",
      action: "Claude review completed",
      actor: "Claude",
      actorType: "claude",
      timestamp: "2024-01-15T10:45:00Z",
    },
  ],
};

export const mockDecisionRoomState: DecisionRoomState = {
  topics: mockDecisionTopics,
  selectedTopicId: "topic-1",
  thread: mockDecisionThread,
  intelligence: mockTopicIntelligence,
};

// ============================================
// Command AURA State
// ============================================

export const mockCommandThread: ConversationMessage[] = [
  {
    id: "cmd-1",
    source: "aura",
    agentName: "AURA",
    content:
      "Good morning. I am monitoring 4 active jobs. One blocker requires your attention: Admin Console deployment is waiting on credential verification.",
    timestamp: "2024-01-15T08:00:00Z",
  },
  {
    id: "cmd-2",
    source: "human",
    content: "What is the status of the admin console deployment?",
    timestamp: "2024-01-15T10:00:00Z",
  },
  {
    id: "cmd-3",
    source: "aura",
    agentName: "AURA",
    content:
      "The admin console is code-complete. Local tests pass. Staging deployment is safe. However, production deployment is blocked due to missing credential references. I recommend holding production until credentials are verified.",
    timestamp: "2024-01-15T10:01:00Z",
  },
  {
    id: "cmd-4",
    source: "evidence",
    content: "Credential scan results show 2 missing references.",
    timestamp: "2024-01-15T10:01:30Z",
    metadata: { evidenceId: "ev-1" },
  },
  {
    id: "cmd-5",
    source: "human",
    content: "Ask Claude to review the credential configuration.",
    timestamp: "2024-01-15T10:30:00Z",
  },
  {
    id: "cmd-6",
    source: "aura",
    agentName: "AURA",
    content: "I have forwarded your request to Claude for deep analysis.",
    timestamp: "2024-01-15T10:30:30Z",
  },
  {
    id: "cmd-7",
    source: "claude",
    agentName: "Claude",
    content:
      "I have completed the credential configuration review. The missing references are for OAuth integration: client_id and client_secret. These must be configured in the production secrets manager before deployment can proceed safely.",
    timestamp: "2024-01-15T10:45:00Z",
  },
];

export const mockCommandContext: CommandContext = {
  currentState: "Production deployment blocked. Staging safe to proceed.",
  relatedDecisionTopic: "Admin Console Production Deployment",
  currentBlocker: "Missing credential references (2 items)",
  recommendedAction: {
    text: "Approve staging deployment, hold production",
    type: "hold",
  },
  memoryNotes: [
    "Previous deployment required similar credential setup",
    "Credential rotation policy: 90 days",
    "Last successful deployment: 2024-01-02",
  ],
};

export const mockSuggestedCommands: SuggestedCommand[] = [
  {
    id: "sug-1",
    text: "Show me the credential scan details",
    targetAgent: "aura",
  },
  {
    id: "sug-2",
    text: "Approve staging deployment",
    targetAgent: "aura",
  },
  {
    id: "sug-3",
    text: "What is blocking production?",
    targetAgent: "aura",
  },
  {
    id: "sug-4",
    text: "Ask Codex to run integration tests",
    targetAgent: "codex",
  },
];

export const mockCommandAuraState: CommandAuraState = {
  selectedAgentId: "aura",
  thread: mockCommandThread,
  context: mockCommandContext,
  suggestedCommands: mockSuggestedCommands,
};
